/**
 * Posição decomposta por tipo × submercado (#34 PR-B) — card ADITIVO na view
 * do PnL, abaixo da Curva Curta/Longa (que seguem INTOCADAS). Spec:
 * docs/mocks/posicao_tipo_submercado_mock.html.
 *
 * - Filtros Tipo/Submercado → /api/posicao-decomposta?tipo=&submercado=
 *   (recorta a curva pro slice; "Todos" = tudo).
 * - "Empilhar por" Submercado|Tipo: barra EMPILHADA por vértice com SINAL —
 *   vendido à esquerda do zero, comprado à direita (zero fixo no meio).
 * - Clicar o vértice abre a MATRIZ tipo × submercado (MWm 2dp, célula com
 *   sinal); a linha TOTAL fecha EXATO com as células (F-34A do backend);
 *   `arredondamento_difere` → nota discreta com mwm_global/mwh_global.
 * - Vértice sem célula no slice fica 0 (não some da lista).
 */
import { Fragment, useMemo, useState } from "react";
import { numBr } from "../../format";
import { usePosicaoDecomposta } from "../../hooks/usePosicaoDecomposta";
import type { PosicaoDecompLinha } from "../../api/types";
import { AsOfBadge } from "../ui/AsOfBadge";

const TIPOS_UI = [
  { value: "", label: "Todos" },
  { value: "CONV", label: "Convencional" },
  { value: "I0", label: "I0" },
  { value: "I5", label: "I5 (I50)" },
  { value: "I100", label: "I100" },
];
const SUBS_UI = ["", "SE", "S", "NE", "N"];

// Cores do mock (submercado) + paleta análoga pra tipo.
const COR_SUB: Record<string, string> = {
  SE: "#00dac8", S: "#4f8cff", NE: "#f0b429", N: "#b06bff",
};
const COR_TIPO: Record<string, string> = {
  Convencional: "#4FB3E8", I0: "#3ddc84", I50: "#f0b429", I100: "#b06bff",
};

type Empilhar = "submercado" | "tipo";

const SELECT_CLS =
  "bg-indra-azul-deep border border-white/10 rounded-md px-3 py-1.5 text-sm text-white " +
  "focus:outline-none focus:border-indra-turquesa/60";

function corValor(v: number): string {
  if (Math.abs(v) < 0.005) return "text-white/40";
  return v >= 0 ? "text-indra-positivo" : "text-indra-negativo";
}

/** Grupos (dimensão de empilhar) com net MWm por vértice — segmento = NET do
 * grupo, lado decidido pelo sinal do net (célula mista dentro do grupo neta).
 * N-B1 (review): ordenados pela ordem FIXA da dimensão (`ordem`), não pela
 * ordem das células (tipo-major) — segmentos consistentes entre vértices. */
function gruposDo(
  l: PosicaoDecompLinha, dim: Empilhar, ordem: string[],
): { chave: string; mwm: number }[] {
  const acc = new Map<string, number>();
  for (const c of l.celulas) {
    const k = dim === "submercado" ? c.submercado : c.tipo_energia;
    acc.set(k, (acc.get(k) ?? 0) + c.mwm);
  }
  return [...acc.entries()]
    .map(([chave, mwm]) => ({ chave, mwm }))
    .sort((a, b) => {
      const ia = ordem.indexOf(a.chave);
      const ib = ordem.indexOf(b.chave);
      return (ia === -1 ? ordem.length : ia) - (ib === -1 ? ordem.length : ib);
    });
}

/** Barra empilhada com sinal: zero FIXO no meio; vendido cresce pra esquerda,
 * comprado pra direita; escala = maior lado da seção (comparável entre vértices). */
function BarraEmpilhada({
  linha, dim, maxLado, cores, ordem,
}: {
  linha: PosicaoDecompLinha;
  dim: Empilhar;
  maxLado: number;
  cores: Record<string, string>;
  ordem: string[];
}) {
  const grupos = gruposDo(linha, dim, ordem);
  const negs = grupos.filter((g) => g.mwm < 0);
  const poss = grupos.filter((g) => g.mwm > 0);
  const w = (v: number) => `${Math.min(100, (Math.abs(v) / (maxLado || 1)) * 100)}%`;
  return (
    <div className="flex items-center h-[16px] min-w-[150px]" aria-hidden>
      <div className="flex-1 flex justify-end h-full rounded-l-[3px] overflow-hidden bg-indra-azul-deep">
        {negs.map((g) => (
          <span
            key={g.chave}
            data-testid={`seg-neg-${linha.vertice}-${g.chave}`}
            title={`${g.chave}: ${numBr(g.mwm, 2)} MWm`}
            style={{ width: w(g.mwm), background: cores[g.chave] ?? "#8ba6bf" }}
            className="h-full block"
          />
        ))}
      </div>
      <div className="w-[2px] h-full bg-white/40" />
      <div className="flex-1 flex h-full rounded-r-[3px] overflow-hidden bg-indra-azul-deep">
        {poss.map((g) => (
          <span
            key={g.chave}
            data-testid={`seg-pos-${linha.vertice}-${g.chave}`}
            title={`${g.chave}: +${numBr(g.mwm, 2)} MWm`}
            style={{ width: w(g.mwm), background: cores[g.chave] ?? "#8ba6bf" }}
            className="h-full block"
          />
        ))}
      </div>
    </div>
  );
}

/** Matriz tipo × submercado (MWm) do vértice clicado — TOTAL fecha EXATO com
 * as células (F-34A: o `mwm` do payload É a Σ das células exibidas). */
function MatrizVertice({
  linha, tipos, submercados,
}: {
  linha: PosicaoDecompLinha;
  tipos: string[];
  submercados: string[];
}) {
  const porChave = new Map(linha.celulas.map((c) => [`${c.tipo_energia}|${c.submercado}`, c.mwm]));
  const totalLinha = (t: string) =>
    Math.round(linha.celulas.filter((c) => c.tipo_energia === t).reduce((s, c) => s + c.mwm, 0) * 100) / 100;
  const totalCol = (s: string) =>
    Math.round(linha.celulas.filter((c) => c.submercado === s).reduce((s2, c) => s2 + c.mwm, 0) * 100) / 100;
  // N-B2 (review): faixa neutra — 0,00 não é comprado nem vendido.
  const celCls = (v: number | undefined) =>
    v === undefined
      ? "text-white/25"
      : Math.abs(v) < 0.005
        ? "text-white/40"
        : v > 0
          ? "bg-indra-positivo/10 text-indra-positivo"
          : "bg-indra-negativo/10 text-indra-negativo";
  return (
    <div className="px-4 py-4 bg-indra-azul-deep/60 border-t border-white/5" data-testid={`matriz-${linha.vertice}`}>
      <p className="text-[11px] text-white/50 mb-2">
        De onde vem — <b className="text-white/80">{linha.vertice}</b> · matriz tipo × submercado (MWm)
      </p>
      <div className="overflow-x-auto">
        <table className="w-auto min-w-[420px]">
          <thead>
            <tr>
              <th className="text-left py-1.5 px-2.5 text-[10px] uppercase tracking-wide text-white/50 border-b border-white/10">Tipo \ Sub</th>
              {submercados.map((s) => (
                <th key={s} className="text-right py-1.5 px-2.5 text-[10px] uppercase tracking-wide text-white/50 border-b border-white/10">{s}</th>
              ))}
              <th className="text-right py-1.5 px-2.5 text-[10px] uppercase tracking-wide text-white/70 border-b border-white/10">Total</th>
            </tr>
          </thead>
          <tbody className="font-mono text-xs">
            {tipos.map((t) => (
              <tr key={t} className="border-b border-white/5">
                <td className="py-1.5 px-2.5 font-sans text-white/70">{t}</td>
                {submercados.map((s) => {
                  const v = porChave.get(`${t}|${s}`);
                  return (
                    <td key={s} className={`py-1.5 px-2.5 text-right tabular-nums ${celCls(v)}`}>
                      {v === undefined ? "—" : numBr(v, 2)}
                    </td>
                  );
                })}
                <td className={`py-1.5 px-2.5 text-right tabular-nums font-semibold ${corValor(totalLinha(t))}`}>
                  {linha.celulas.some((c) => c.tipo_energia === t) ? numBr(totalLinha(t), 2) : "—"}
                </td>
              </tr>
            ))}
            <tr data-testid={`matriz-total-${linha.vertice}`}>
              <td className="py-1.5 px-2.5 font-sans text-white font-semibold">Total sub</td>
              {submercados.map((s) => (
                <td key={s} className={`py-1.5 px-2.5 text-right tabular-nums font-semibold ${corValor(totalCol(s))}`}>
                  {linha.celulas.some((c) => c.submercado === s) ? numBr(totalCol(s), 2) : "—"}
                </td>
              ))}
              {/* F-34A: o mwm do payload É Σ das células exibidas — fecha exato.
                  GO 03/08: null (fora da cobertura do feed) → "—". */}
              <td className={`py-1.5 px-2.5 text-right tabular-nums font-bold ${corValor(linha.mwm ?? 0)}`}>
                {linha.mwm === null ? "—" : numBr(linha.mwm, 2)}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      {linha.arredondamento_difere && (
        <p className="text-[10px] text-white/40 mt-2" data-testid={`matriz-nota-${linha.vertice}`}>
          {/* só renderiza com arredondamento_difere (nunca no fora-de-cobertura,
              onde os globais são null) — o ?? 0 é só pro typecheck */}
          reconcilia com a Curva (arredondamento): MWm {numBr(linha.mwm_global ?? 0, 2)} ·
          MWh {numBr(linha.mwh_global ?? 0, 0)}
        </p>
      )}
    </div>
  );
}

function SecaoCurva({
  titulo, linhas, dim, cores, aberto, onToggle, tipos, submercados,
}: {
  titulo: string;
  linhas: PosicaoDecompLinha[];
  dim: Empilhar;
  cores: Record<string, string>;
  aberto: string | null;
  onToggle: (v: string) => void;
  tipos: string[];
  submercados: string[];
}) {
  const ordem = dim === "submercado" ? submercados : tipos;
  // Escala da barra: maior LADO (Σ|negs| ou Σposs) da seção inteira.
  const maxLado = useMemo(() => {
    let m = 0;
    for (const l of linhas) {
      const gs = gruposDo(l, dim, ordem);
      m = Math.max(
        m,
        Math.abs(gs.filter((g) => g.mwm < 0).reduce((s, g) => s + g.mwm, 0)),
        gs.filter((g) => g.mwm > 0).reduce((s, g) => s + g.mwm, 0),
      );
    }
    return m;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linhas, dim, tipos, submercados]);

  return (
    <div>
      <p className="text-[10px] uppercase tracking-wide text-white/50 font-medium mb-1">{titulo}</p>
      <table className="w-full">
        <thead>
          <tr>
            <th className="text-left py-1.5 px-2.5 text-[10px] uppercase tracking-wide text-white/50 border-b border-white/10 w-24">Vértice</th>
            <th className="text-right py-1.5 px-2.5 text-[10px] uppercase tracking-wide text-white/50 border-b border-white/10 w-28">MWm (net)</th>
            <th className="text-left py-1.5 px-2.5 text-[10px] uppercase tracking-wide text-white/50 border-b border-white/10">
              Composição (vendido ◂ 0 ▸ comprado)
            </th>
          </tr>
        </thead>
        <tbody>
          {linhas.map((l) => {
            const open = aberto === l.vertice;
            return (
              <Fragment key={l.vertice}>
                <tr
                  data-testid={`vertice-${l.vertice}`}
                  className="border-b border-white/5 hover:bg-white/5 cursor-pointer"
                  onClick={() => onToggle(l.vertice)}
                >
                  <td className="py-2 px-2.5 text-sm text-white">
                    <span className="inline-block w-3 text-white/40">{open ? "▾" : "▸"}</span>
                    {l.vertice}
                  </td>
                  <td className={`py-2 px-2.5 text-right text-sm font-mono tabular-nums ${corValor(l.mwm ?? 0)}`}>
                    {l.mwm === null ? "—" : numBr(l.mwm, 2)}
                  </td>
                  <td className="py-2 px-2.5">
                    <BarraEmpilhada linha={l} dim={dim} maxLado={maxLado} cores={cores} ordem={ordem} />
                  </td>
                </tr>
                {open && (
                  <tr>
                    <td colSpan={3} className="p-0">
                      <MatrizVertice linha={l} tipos={tipos} submercados={submercados} />
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export function PosicaoDecomposta() {
  const [tipo, setTipo] = useState<string>("");
  const [sub, setSub] = useState<string>("");
  const [dim, setDim] = useState<Empilhar>("submercado");
  const [aberto, setAberto] = useState<string | null>(null);

  const { data, isLoading, error } = usePosicaoDecomposta(tipo || null, sub || null);

  const cores = dim === "submercado" ? COR_SUB : COR_TIPO;
  const legenda = dim === "submercado"
    ? (data?.dimensoes.submercados ?? [])
    : (data?.dimensoes.tipos ?? []);

  return (
    <div className="bg-indra-azul-surface rounded-lg border border-white/10 p-4" data-testid="posicao-decomposta">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-1">
        <div>
          <h3 className="text-white font-semibold text-sm">Posição — por tipo de energia e submercado</h3>
          <p className="text-white/40 text-[11px] mt-0.5">
            o global esconde a forma do risco: vendido Conv/SE e comprado I100/NE se cancelam no net
          </p>
        </div>
        {/* #60: as-of do DADO servido (snapshot diário do precompute / live) —
            a matriz muda a cada trade; o rótulo diz de quando o número é. */}
        {data && <AsOfBadge asOf={data.as_of ?? null} />}
      </div>

      {/* Filtros + empilhar */}
      <div className="flex items-center gap-3 flex-wrap mt-3 mb-4">
        <label className="flex items-center gap-2 text-[11px] text-white/50">
          Tipo de energia
          <select
            data-testid="pd-filtro-tipo"
            value={tipo}
            onChange={(e) => setTipo(e.target.value)}
            className={SELECT_CLS}
          >
            {TIPOS_UI.map((t) => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
        </label>
        <label className="flex items-center gap-2 text-[11px] text-white/50">
          Submercado
          <select
            data-testid="pd-filtro-sub"
            value={sub}
            onChange={(e) => setSub(e.target.value)}
            className={SELECT_CLS}
          >
            {SUBS_UI.map((s) => (
              <option key={s} value={s}>{s === "" ? "Todos" : s}</option>
            ))}
          </select>
        </label>
        <div className="flex items-center gap-2 text-[11px] text-white/50 ml-auto">
          Empilhar por
          <div className="inline-flex rounded-md border border-white/10 bg-indra-azul-deep p-0.5 text-xs" role="group" aria-label="Empilhar por">
            <button
              type="button"
              onClick={() => setDim("submercado")}
              aria-pressed={dim === "submercado"}
              className={`px-3 py-1 rounded ${dim === "submercado" ? "bg-white/15 text-white font-semibold" : "text-white/50 hover:text-white/80"}`}
            >
              Submercado
            </button>
            <button
              type="button"
              onClick={() => setDim("tipo")}
              aria-pressed={dim === "tipo"}
              className={`px-3 py-1 rounded ${dim === "tipo" ? "bg-white/15 text-white font-semibold" : "text-white/50 hover:text-white/80"}`}
            >
              Tipo
            </button>
          </div>
        </div>
      </div>

      {isLoading && <p className="text-white/50 text-sm" data-testid="pd-loading">Carregando decomposição…</p>}
      {error && (
        <p className="text-indra-negativo text-sm" data-testid="pd-erro">
          Falha ao carregar a decomposição: {error}
        </p>
      )}

      {data && !isLoading && !error && (
        <div className="space-y-5">
          <SecaoCurva
            titulo="Curva Curta (mensal)"
            linhas={data.curta.linhas}
            dim={dim}
            cores={cores}
            aberto={aberto}
            onToggle={(v) => setAberto(aberto === v ? null : v)}
            tipos={data.dimensoes.tipos}
            submercados={data.dimensoes.submercados}
          />
          <SecaoCurva
            titulo="Curva Longa (anual)"
            linhas={data.longa.linhas}
            dim={dim}
            cores={cores}
            aberto={aberto}
            onToggle={(v) => setAberto(aberto === v ? null : v)}
            tipos={data.dimensoes.tipos}
            submercados={data.dimensoes.submercados}
          />
          {/* Legenda da dimensão empilhada */}
          <div className="flex gap-4 flex-wrap text-[11px] text-white/60" data-testid="pd-legenda">
            {legenda.map((k) => (
              <span key={k} className="inline-flex items-center gap-1.5">
                <i className="w-[10px] h-[10px] rounded-[2px] inline-block" style={{ background: cores[k] ?? "#8ba6bf" }} />
                {k}
              </span>
            ))}
            <span className="text-white/35">· vendido à esquerda do zero · comprado à direita · clique o vértice pra matriz</span>
          </div>
          <p className="text-[10px] text-white/35 leading-relaxed">
            View re-agrupada das mesmas células da Curva (mês × submercado × tipo) — Σ células
            de um vértice fecha exato com o net exibido; sob filtro, o net é o do recorte.
          </p>
        </div>
      )}
    </div>
  );
}
